import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import aumhumLogo from 'src/assets/logo_brand.png'
import { useIdleScreen } from 'src/customHooks/customHooks'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { IdlePopupaStyledDialog } from 'src/clinic/components/CheckIn/components/styledComponents'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { selectTeacherProfileData } from 'src/reducers/teacherProfileData/teacherProfileDataReducer'
import { getActivePeekIns } from '../utils/peekInManager'
import {
  logMentorIdle,
  presentInRoom,
  refreshCheckIntoRoomWithLocalStorageApplied,
} from 'src/clinic/components/CheckIn/services'

dayjs.extend(utc)

function readCheckedInDetails() {
  try {
    const item = localStorage.getItem('checkedInDetails')
    return item ? JSON.parse(item) : null
  } catch {
    return null
  }
}

function firstTruthy(...values) {
  return values.find((value) => {
    if (typeof value === 'string') {
      return value.trim().length > 0
    }
    return Boolean(value)
  })
}

function decodeMaybeEncodedTimezone(timezone) {
  if (!timezone || typeof timezone !== 'string') {
    return timezone
  }

  if (!timezone.includes('%')) {
    return timezone
  }

  try {
    return decodeURIComponent(timezone)
  } catch {
    return null
  }
}

function resolveTimezoneValue(timeZoneData) {
  if (!timeZoneData) {
    return null
  }

  if (typeof timeZoneData === 'string') {
    return timeZoneData
  }

  return timeZoneData?.offset || timeZoneData?.gmtvalue || timeZoneData?.timeZone || null
}

function resolveClinicTimezone(clinicLocationData, checkedInDetails, currentPeekIn) {
  const timezone = firstTruthy(
    clinicLocationData?.schedules?.[0]?.timezone,
    clinicLocationData?.schedules?.[0]?.timeZone,
    checkedInDetails?.schedules?.[0]?.timezone,
    checkedInDetails?.schedules?.[0]?.timeZone,
    currentPeekIn?.schedules?.[0]?.timezone,
    currentPeekIn?.schedules?.[0]?.timeZone,
    resolveTimezoneValue(clinicLocationData?.timeZone),
    resolveTimezoneValue(checkedInDetails?.clinicLocationTimeZone),
    resolveTimezoneValue(checkedInDetails?.timeZone),
    resolveTimezoneValue(currentPeekIn?.clinicLocationTimeZone),
    resolveTimezoneValue(currentPeekIn?.timeZone),
  )

  return decodeMaybeEncodedTimezone(timezone) || '+05:30'
}

function resolvePractitionerName(profileData, checkedInDetails, currentPeekIn) {
  const fullName = [profileData?.firstName, profileData?.lastName].filter(Boolean).join(' ')

  return (
    firstTruthy(
      profileData?.name,
      profileData?.title,
      fullName,
      profileData?.fullName,
      checkedInDetails?.mentorName,
      checkedInDetails?.teacherName,
      checkedInDetails?.practitionerName,
      checkedInDetails?.doctorName,
      checkedInDetails?.checkedInMentorName,
      checkedInDetails?.mentor?.name,
      checkedInDetails?.teacher?.name,
      currentPeekIn?.mentorName,
      currentPeekIn?.teacherName,
      currentPeekIn?.practitionerName,
      currentPeekIn?.doctorName,
      currentPeekIn?.checkedInMentorName,
    ) || 'Doctor'
  )
}

function resolveBrandLogo(clinicLocationData, checkedInDetails, currentPeekIn) {
  const brandData = firstTruthy(
    clinicLocationData?.clinicBrandData,
    checkedInDetails?.clinicBrandData,
    currentPeekIn?.clinicBrandData,
  )

  return getImageLink(
    firstTruthy(
      brandData?.coverIcon,
      brandData?.icon,
      brandData?.clinicBrandCoverIcon,
      brandData?.clinicBrandIconImage,
      clinicLocationData?.clinicBrandCoverIcon,
      clinicLocationData?.clinicBrandIcon,
      clinicLocationData?.clinicBrandIconImage,
      checkedInDetails?.clinicBrandCoverIcon,
      checkedInDetails?.clinicBrandIcon,
      checkedInDetails?.clinicBrandIconImage,
      currentPeekIn?.clinicBrandCoverIcon,
      currentPeekIn?.clinicBrandIcon,
      currentPeekIn?.clinicBrandIconImage,
    ),
    firstTruthy(
      brandData?.coverImage,
      brandData?.clinicBrandCoverImage,
      clinicLocationData?.clinicBrandCoverImage,
      checkedInDetails?.clinicBrandCoverImage,
      checkedInDetails?.clinicBrandImage,
      currentPeekIn?.clinicBrandCoverImage,
      currentPeekIn?.clinicBrandImage,
    ),
    false,
  )
}

function formatClinicTime(dateTime, clinicTimezone, format = 'h:mm A') {
  if (!dateTime) {
    return ''
  }

  return dayjs.utc(dateTime).utcOffset(clinicTimezone || '+05:30').format(format)
}

/**
 * Check-in/Peek-in Idle Popup Component
 * Shows when user is idle while checked in or peeked in
 * Can be manually triggered via lock icon
 */
export function CheckInIdlePopup({ 
  clinicLocationData,
  manualTrigger = false,
  onManualTrigger,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // This popup is mounted at the app root (EmrCheckInIdleManager), which is OUTSIDE
  // the MentorProfileContext.Provider that lives inside the mentor layout — so the
  // context is its null default here. mentorProfileData is only used for an optional
  // greeting name, so default to {} rather than crashing the whole tree.
  const { mentorProfileData } = useContext(MentorProfileContext) ?? {}
  const mentorProfileStoreData = useSelector(selectTeacherProfileData)
  const resolvedMentorProfileData = {
    ...(mentorProfileStoreData?.data || {}),
    ...(mentorProfileData || {}),
  }
  const [checkedInDetails, setCheckedInDetails] = useState(readCheckedInDetails)
  const [activePeekIns, setActivePeekIns] = useState(() => getActivePeekIns())
  const [idleLogKey, setIdleLogKey] = useState(null)
  const [currentTime, setCurrentTime] = useState(() => dayjs())
  const activityDismissRef = useRef(false)

  // Find peek-in for current clinic
  const currentPeekIn = useMemo(() => {
    if (!clinicLocationData?.id) return null
    return activePeekIns.find((peekIn) => peekIn.clinicLocationId === clinicLocationData.id)
  }, [activePeekIns, clinicLocationData?.id])

  const isCheckedIn = !!checkedInDetails
  const isPeekedIn = !!currentPeekIn
  const hasAccess = isCheckedIn || isPeekedIn

  // Use idle screen hook - 3 minutes of inactivity
  const { isIdle, resetIdle } = useIdleScreen({ idleTime: 180000, manualReset: true })
  
  // Show popup if manually triggered or automatically idle
  const showPopup = manualTrigger || (isIdle && hasAccess)

  const currentRoomName =
    checkedInDetails?.roomName ||
    checkedInDetails?.chamberRoomTitle ||
    currentPeekIn?.title ||
    currentPeekIn?.name ||
    'Room'
  const clinicName = clinicLocationData?.name || clinicLocationData?.title || 'Clinic'
  const practitionerName = resolvePractitionerName(
    resolvedMentorProfileData,
    checkedInDetails,
    currentPeekIn,
  )
  const clinicTimezone = resolveClinicTimezone(clinicLocationData, checkedInDetails, currentPeekIn)
  const clinicCurrentTime = currentTime.utcOffset(clinicTimezone)
  const brandLogo = resolveBrandLogo(clinicLocationData, checkedInDetails, currentPeekIn)
  const checkInDisplayTime = formatClinicTime(
    checkedInDetails?.createdTime || currentPeekIn?.peekedInAt,
    clinicTimezone,
  )

  const logAndResetIdle = useCallback(async () => {
    if (checkedInDetails) {
      await refreshCheckIntoRoomWithLocalStorageApplied(checkedInDetails)
    } else if (currentPeekIn) {
      presentInRoom(currentPeekIn)
    }
    resetIdle()
    if (onManualTrigger) {
      onManualTrigger(false)
    }
  }, [checkedInDetails, currentPeekIn, onManualTrigger, resetIdle])

  useEffect(() => {
    const refreshLocalAccessState = () => {
      setCheckedInDetails(readCheckedInDetails())
      setActivePeekIns(getActivePeekIns())
    }

    const intervalId = setInterval(refreshLocalAccessState, 10000)
    window.addEventListener('storage', refreshLocalAccessState)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, refreshLocalAccessState)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, refreshLocalAccessState)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('storage', refreshLocalAccessState)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, refreshLocalAccessState)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, refreshLocalAccessState)
    }
  }, [])

  useEffect(() => {
    if (!isIdle || manualTrigger || !hasAccess) {
      setIdleLogKey(null)
      return
    }

    const sourceDetails = checkedInDetails || currentPeekIn
    const nextIdleLogKey = `${sourceDetails?.chamberRoomId || sourceDetails?.id || 'room'}:${
      checkedInDetails ? 'checked-in' : 'peeked-in'
    }`
    if (idleLogKey === nextIdleLogKey) {
      return
    }

    if (sourceDetails) {
      logMentorIdle(sourceDetails)
      setIdleLogKey(nextIdleLogKey)
    }
  }, [checkedInDetails, currentPeekIn, hasAccess, idleLogKey, isIdle, manualTrigger])

  useEffect(() => {
    if (!showPopup) {
      activityDismissRef.current = false
      return undefined
    }

    setCurrentTime(dayjs())
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs())
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [showPopup])

  useEffect(() => {
    if (!showPopup) {
      return undefined
    }

    activityDismissRef.current = false
    let armed = false
    const armTimer = setTimeout(() => {
      armed = true
    }, 350)

    const dismissOnActivity = () => {
      if (!armed || activityDismissRef.current) {
        return
      }
      activityDismissRef.current = true
      logAndResetIdle()
    }

    window.addEventListener('mousemove', dismissOnActivity)
    window.addEventListener('keydown', dismissOnActivity)
    window.addEventListener('wheel', dismissOnActivity, { passive: true })
    window.addEventListener('touchstart', dismissOnActivity, { passive: true })

    return () => {
      clearTimeout(armTimer)
      window.removeEventListener('mousemove', dismissOnActivity)
      window.removeEventListener('keydown', dismissOnActivity)
      window.removeEventListener('wheel', dismissOnActivity)
      window.removeEventListener('touchstart', dismissOnActivity)
    }
  }, [logAndResetIdle, showPopup])

  // Don't show if not checked in or peeked in (unless manually triggered)
  if (!hasAccess && !manualTrigger) {
    return null
  }

  return (
    <IdlePopupaStyledDialog
      open={showPopup}
      fullScreen
      maxWidth={false}
      fullWidth
      disableEscapeKeyDown={false}
      onClose={logAndResetIdle}
      BackdropProps={{
        sx: {
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.5) 0%, rgba(79, 70, 229, 0.26) 52%, rgba(20, 184, 166, 0.18) 100%)',
          backdropFilter: 'blur(26px) saturate(1.45)',
          WebkitBackdropFilter: 'blur(26px) saturate(1.45)',
        },
      }}
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: 0,
          borderRadius: 0,
          background:
            'radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.22), transparent 32%), radial-gradient(circle at 82% 24%, rgba(125, 211, 252, 0.2), transparent 34%), linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',
          color: '#ffffff',
          boxShadow: 'none',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: { xs: 3, sm: 5, md: 8 },
          py: { xs: 4, sm: 6 },
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 18, sm: 26, md: 30 },
            left: { xs: 18, sm: 28, md: 36 },
            right: { xs: 18, sm: 28, md: 36 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pointerEvents: 'none',
          }}
        >
          <Box
            component="img"
            src={aumhumLogo}
            alt="AUMHUM"
            sx={{
              width: { xs: 54, sm: 64 },
              height: { xs: 54, sm: 64 },
              objectFit: 'contain',
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.16)',
              border: '1px solid rgba(255, 255, 255, 0.22)',
              boxShadow: '0 12px 36px rgba(15, 23, 42, 0.16)',
              p: 0.8,
            }}
          />
          {brandLogo && (
            <Box
              component="img"
              src={brandLogo}
              alt={clinicName}
              sx={{
                width: { xs: 54, sm: 64 },
                height: { xs: 54, sm: 64 },
                objectFit: 'cover',
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.18)',
                border: '1px solid rgba(255, 255, 255, 0.24)',
                boxShadow: '0 12px 36px rgba(15, 23, 42, 0.16)',
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            position: 'absolute',
            top: { xs: 92, sm: 36 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 'calc(100% - 48px)', sm: 'calc(100% - 220px)' },
            color: 'rgba(255, 255, 255, 0.72)',
            fontSize: { xs: '12px', sm: '13px' },
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}
        >
          Move mouse or press any key to continue
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2.5, sm: 3.5, md: 4 },
            width: '100%',
            maxWidth: '980px',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '58px', sm: '86px', md: '118px' },
              fontWeight: 800,
              lineHeight: 0.95,
              letterSpacing: 0,
              textShadow: '0 18px 50px rgba(15, 23, 42, 0.34)',
            }}
          >
            {clinicCurrentTime.format(isMobile ? 'h:mm' : 'h:mm A')}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.92)',
              fontSize: { xs: '22px', sm: '32px', md: '42px' },
              fontWeight: 750,
              lineHeight: 1.15,
              maxWidth: '920px',
              textShadow: '0 10px 28px rgba(15, 23, 42, 0.26)',
            }}
          >
            {practitionerName}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 1, sm: 1.5 },
              color: 'rgba(255, 255, 255, 0.76)',
              fontSize: { xs: '14px', sm: '17px', md: '19px' },
              fontWeight: 650,
              lineHeight: 1.4,
            }}
          >
            <Box component="span">{clinicName}</Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, opacity: 0.48 }}>
              /
            </Box>
            <Box component="span">{currentRoomName}</Box>
          </Box>
          {checkInDisplayTime && (
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.58)',
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 650,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {isCheckedIn ? 'Checked in' : 'Peeked in'} at {checkInDisplayTime}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 24, sm: 34 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 140, sm: 180 },
            height: 3,
            borderRadius: '999px',
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.58) 50%, transparent 100%)',
            opacity: 0.72,
          }}
        />
      </Box>
    </IdlePopupaStyledDialog>
  )
}
